import { useEffect, useMemo, useRef, useState } from "react";

import { useAtom } from "jotai";
import {
  ArrowRight,
  ChartNoAxesCombined,
  ChevronDown,
  Compass,
  Database,
  EyeOff,
  Info,
  Lightbulb,
  ListOrdered,
  MessageSquare,
  MoreHorizontal,
  Package,
  Sparkles,
  Target,
  TimerReset,
  TrendingUpDown,
} from "lucide-react";

import { Link, routes } from "@redwoodjs/router";

import { DataExportButton } from "src/components/dataView/DataExport/DataExportButton";
import { MetricGlossaryLabel } from "src/components/MetricGlossaryLabel/MetricGlossaryLabel";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "src/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "src/components/ui/responsive-dialog";
import { ScrollArea } from "src/components/ui/scroll-area";
import { Skeleton } from "src/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  assistantLaunchRequestAtom,
  createAssistantPromptRequest,
} from "src/lib/assistantLaunch";
import { storeIntegrationTypes } from "src/lib/constants";
import {
  buildDashboardInsightActionRequest,
  compareDashboardInsightsByPriority,
  formatDashboardInsightLabel,
  formatDashboardInsightRelativeTime,
  formatDashboardInsightText,
  getDashboardInsightChangeMeta,
  getDashboardInsightDataContext,
  getDashboardInsightFacts,
  getDashboardInsightMetricTarget,
  getDashboardInsightPayloadSections,
  getDashboardInsightPrimaryComparison,
  getDashboardInsightPriorityMeta,
  getDashboardInsightRangeSummary,
  getDashboardInsightTypeMeta,
} from "src/lib/dashboardInsights";
import { normalizeCurrency } from "src/lib/format";
import { useApiMutation, useApiQuery } from "src/lib/hooks/useApi";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { useIsMobile } from "src/lib/hooks/useMobile";
import {
  DISMISS_STORE_INSIGHT_MUTATION,
  SNOOZE_STORE_INSIGHT_MUTATION,
  STORE_INSIGHTS_QUERY,
} from "src/lib/queries/insights";
import { cn } from "src/lib/utils";

const DASHBOARD_INSIGHT_LIMIT = 12;

const toneConfig = {
  default: {
    icon: Lightbulb,
    cardClassName: "hover:border-primary/40",
    badgeClassName: "border-primary/30 bg-primary/5 text-primary",
    iconClassName: "text-primary",
  },
  scenario: {
    icon: Compass,
    cardClassName: "hover:border-sky-500/40",
    badgeClassName:
      "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    iconClassName: "text-sky-700 dark:text-sky-300",
  },
};

const getRouteHref = (target) => {
  const routeBuilder = target?.routeName ? routes[target.routeName] : null;
  if (!routeBuilder) return null;

  return routeBuilder(target.routeParams || {});
};

const priorityBadgeClassName = {
  high: "border-red-500/35 bg-red-500/10 text-red-700 dark:text-red-300",
  medium:
    "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  low: "border-border bg-muted text-muted-foreground",
};

const DashboardInsightPriorityBadge = ({ insight }) => {
  const priorityMeta = getDashboardInsightPriorityMeta(insight);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn("gap-1", priorityBadgeClassName[priorityMeta.band])}
        >
          {priorityMeta.bandLabel}
        </Badge>
      </TooltipTrigger>
      <TooltipContent sideOffset={6}>
        {priorityMeta.bandLabel} priority insight
      </TooltipContent>
    </Tooltip>
  );
};

const DashboardInsightTypeBadge = ({
  icon: Icon,
  label,
  description,
  className,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          tabIndex={-1}
          className={cn(
            "inline-flex size-5 shrink-0 items-center justify-center rounded-sm bg-transparent p-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            className
          )}
        >
          <Icon className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent sideOffset={6} className="max-w-64 text-xs">
        {description || label}
      </TooltipContent>
    </Tooltip>
  );
};

const DashboardInsightChangeBadge = ({ insight }) => {
  const changeMeta = getDashboardInsightChangeMeta(insight);
  if (!changeMeta) return null;

  if (changeMeta.label === "New") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="New insight"
            tabIndex={-1}
            className="inline-flex size-5 items-center justify-center rounded-sm bg-transparent p-0 text-emerald-500 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:text-emerald-300"
          >
            <Sparkles className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>
          {changeMeta.description || "New insight"}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          aria-label={`${changeMeta.label} insight`}
          tabIndex={-1}
          className={cn("gap-1", changeMeta.className)}
        >
          {changeMeta.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent sideOffset={6} className="max-w-64 text-xs">
        {changeMeta.description || changeMeta.label}
      </TooltipContent>
    </Tooltip>
  );
};

const DashboardInsightActionsMenu = ({
  insight,
  onSnoozeInsight,
  onDismissInsight,
  align = "end",
}) => {
  if (!onSnoozeInsight && !onDismissInsight) return null;

  const title = formatDashboardInsightText(insight?.title) || "insight";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative z-30 size-7 shrink-0 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          aria-label={`Insight actions for ${title}`}
          onClick={(event) => event.stopPropagation()}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="z-[70]">
        {onSnoozeInsight ? (
          <DropdownMenuItem
            onSelect={() => {
              onSnoozeInsight(insight);
            }}
          >
            <TimerReset className="size-4" />
            Snooze 7 days
          </DropdownMenuItem>
        ) : null}
        {onDismissInsight ? (
          <DropdownMenuItem
            onSelect={() => {
              onDismissInsight(insight);
            }}
          >
            <EyeOff className="size-4" />
            Dismiss until changed
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DashboardInsightsSkeleton = () => (
  <section className="space-y-2">
    <div className="flex items-center">
      <Skeleton className="h-5 w-28" />
    </div>
    <div className="flex gap-3 overflow-hidden pb-1">
      {[0, 1, 2].map((index) => (
        <Card key={index} className="w-80 shrink-0 gap-3 py-3">
          <CardHeader className="gap-2 px-4">
            <Skeleton className="h-5 w-24 rounded-full" />
            <Skeleton className="h-4 w-4/5" />
            <div className="space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </CardHeader>
          <CardContent className="flex gap-2 px-4">
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-7 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  </section>
);

export const DashboardInsightCard = ({
  insight,
  onOpenInsight,
  onAskInsight,
  onSnoozeInsight,
  onDismissInsight,
}) => {
  const typeMeta = getDashboardInsightTypeMeta(insight.type);
  const tone = toneConfig[typeMeta.tone] || toneConfig.default;
  const Icon = tone.icon;
  const metricTarget = getDashboardInsightMetricTarget(insight);
  const metricHref = getRouteHref(metricTarget);
  const askRequest = buildDashboardInsightActionRequest(insight);
  const title = formatDashboardInsightText(insight.title);
  const summary = formatDashboardInsightText(insight.summary);
  const contextSummary = getDashboardInsightRangeSummary(insight);

  return (
    <Card
      className={cn(
        "relative w-[20rem] shrink-0 cursor-pointer gap-3 overflow-hidden py-3 transition-colors sm:w-[22rem]",
        tone.cardClassName
      )}
    >
      <button
        type="button"
        aria-label={`Open insight details for ${title}`}
        className="absolute inset-0 z-10 cursor-pointer rounded-lg outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
        onClick={() => onOpenInsight?.(insight)}
      />
      <CardHeader className="pointer-events-none relative z-20 min-w-0 gap-2 px-4">
        <div className="flex min-w-0 max-w-full items-start justify-between gap-2">
          <div className="pointer-events-auto flex min-w-0 max-w-full flex-wrap items-center gap-2">
            <DashboardInsightTypeBadge
              icon={Icon}
              label={typeMeta.label}
              description={typeMeta.description}
              className={tone.iconClassName}
            />
            <DashboardInsightPriorityBadge insight={insight} />
            <DashboardInsightChangeBadge insight={insight} />
          </div>
          <div className="pointer-events-auto shrink-0">
            <DashboardInsightActionsMenu
              insight={insight}
              onSnoozeInsight={onSnoozeInsight}
              onDismissInsight={onDismissInsight}
            />
          </div>
        </div>

        <div className="min-w-0 max-w-full space-y-1.5 overflow-hidden">
          <CardTitle className="line-clamp-2 min-w-0 max-w-full break-words text-sm leading-snug whitespace-normal">
            {title}
          </CardTitle>
          <CardDescription className="line-clamp-2 min-w-0 break-words text-xs leading-relaxed whitespace-normal">
            {summary}
          </CardDescription>
          {contextSummary ? (
            <div className="flex min-w-0 max-w-full items-center gap-1 overflow-hidden text-[11px] text-muted-foreground">
              <Database className="size-3 shrink-0" />
              <span className="min-w-0 truncate">{contextSummary}</span>
            </div>
          ) : null}
        </div>
      </CardHeader>

      {(metricHref || askRequest) && (
        <CardContent className="pointer-events-none relative z-20 mt-auto flex min-w-0 flex-wrap items-center gap-2 overflow-hidden px-4 sm:px-4">
          {metricHref ? (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="pointer-events-auto max-w-full gap-2"
            >
              <Link to={metricHref}>
                {metricTarget.label}
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          ) : null}
          {askRequest ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="pointer-events-auto max-w-full gap-2"
              onClick={(event) => {
                event.stopPropagation();
                onAskInsight?.(askRequest);
              }}
            >
              <MessageSquare className="size-3.5" />
              Ask AI
            </Button>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
};

const getInsightFacts = (insight) =>
  getDashboardInsightFacts(insight).slice(0, 6);

const formatDetailNumber = (value, options = {}) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    ...options,
  }).format(number);
};

const formatDetailPercent = (value, options = {}) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    style: "percent",
    ...options,
  }).format(number);
};

const formatProductSales = (row) =>
  formatDetailNumber(
    row?.totalSales,
    row?.currency
      ? {
          currency: normalizeCurrency(row.currency),
          maximumFractionDigits: 0,
          style: "currency",
        }
      : { maximumFractionDigits: 0 }
  );

const getComparisonPeriodLabel = (row) =>
  row?.period || row?.month || row?.week || "";

const percentageComparisonMetricKeys = new Set([
  "discountRatePct",
  "grossMarginPct",
  "newCustomerConversionProxy",
]);

const countComparisonMetricKeys = new Set([
  "adConversions",
  "newCustomers",
  "orderCount",
]);

const multipleComparisonMetricKeys = new Set(["mer"]);

const currencyLikeComparisonMetricKeys = new Set([
  "aov",
  "costPerAdConversion",
  "marketingSpend",
  "ncac",
  "totalSales",
]);

const formatComparisonPeriodLabel = (row) => {
  const rawPeriod = getComparisonPeriodLabel(row);
  if (!rawPeriod) return "";

  const monthMatch = String(rawPeriod).match(/^(\d{4})-(\d{2})$/);
  if (monthMatch && row?.periodType !== "week" && !row?.week) {
    const [, year, month] = monthMatch;

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      timeZone: "UTC",
      year: "numeric",
    }).format(new Date(Date.UTC(Number(year), Number(month) - 1, 1)));
  }

  const weekMatch = String(rawPeriod).match(/^(\d{4})-(\d{2})$/);
  if (weekMatch && (row?.periodType === "week" || row?.week)) {
    return `Week ${Number(weekMatch[2])}, ${weekMatch[1]}`;
  }

  return rawPeriod;
};

const formatComparisonValue = (candidate, row) => {
  const metricKey = candidate?.metricKey;
  const value = row?.[metricKey];
  const currency = candidate?.currency || row?.currency;

  if (percentageComparisonMetricKeys.has(metricKey)) {
    return formatDetailPercent(value);
  }

  if (multipleComparisonMetricKeys.has(metricKey)) {
    const formatted = formatDetailNumber(value);
    return formatted ? `${formatted}x` : "";
  }

  if (countComparisonMetricKeys.has(metricKey)) {
    return formatDetailNumber(value, { maximumFractionDigits: 0 });
  }

  if (currency && currencyLikeComparisonMetricKeys.has(metricKey)) {
    return formatDetailNumber(value, {
      currency: normalizeCurrency(currency),
      maximumFractionDigits: metricKey === "totalSales" ? 0 : 2,
      style: "currency",
    });
  }

  if (metricKey === "totalSales") {
    return formatDetailNumber(value, { maximumFractionDigits: 0 });
  }

  return formatDetailNumber(value);
};

const formatComparisonSummary = (candidate) => {
  if (!candidate) return "";
  if (candidate.status !== "AVAILABLE") {
    return candidate.reason || "Not enough data";
  }

  const currentPeriod = formatComparisonPeriodLabel(candidate.current);
  const baselinePeriod = formatComparisonPeriodLabel(candidate.baseline);
  const currentValue = formatComparisonValue(candidate, candidate.current);
  const baselineValue = formatComparisonValue(candidate, candidate.baseline);
  const delta = formatDetailPercent(candidate.deltaPct, {
    signDisplay: "exceptZero",
  });

  return [
    currentPeriod && baselinePeriod
      ? `${currentPeriod} vs ${baselinePeriod}`
      : null,
    currentValue && baselineValue
      ? `${currentValue} vs ${baselineValue}`
      : null,
    delta || null,
    candidate.direction && candidate.direction !== "flat"
      ? candidate.direction
      : null,
  ]
    .filter(Boolean)
    .join(" - ");
};

const comparisonLensRank = {
  mom: 0,
  yoy: 1,
  wow: 2,
  best_recent: 3,
};

const getComparisonDirectionVerb = (candidate) => {
  if (candidate?.direction === "worse") return "worsened";
  if (candidate?.direction === "better") return "improved";
  return "changed";
};

const getComparisonDirectionLabel = (candidate) => {
  if (candidate?.direction === "worse") return "worse";
  if (candidate?.direction === "better") return "better";
  return "flat";
};

const getComparisonDirectionClassName = (candidate) => {
  if (candidate?.direction === "worse") {
    return "text-red-700 dark:text-red-300";
  }
  if (candidate?.direction === "better") {
    return "text-emerald-700 dark:text-emerald-300";
  }
  return "text-muted-foreground";
};

const formatSelectedComparisonSummary = (primaryComparison) => {
  const selected = primaryComparison?.selected;
  if (!selected) return "";

  const metricLabel =
    selected.metricLabel ||
    primaryComparison.metricLabel ||
    primaryComparison.fact?.label ||
    "Metric";
  const baselinePeriod = formatComparisonPeriodLabel(selected.baseline);
  const currentValue = formatComparisonValue(selected, selected.current);
  const baselineValue = formatComparisonValue(selected, selected.baseline);
  const delta = formatDetailPercent(selected.deltaPct, {
    signDisplay: "exceptZero",
  });
  const lensLabel =
    selected.lens === "best_recent"
      ? "best recent"
      : selected.label || selected.lens;
  const comparisonLabel = [lensLabel, baselinePeriod].filter(Boolean).join(" ");

  return `${metricLabel} ${getComparisonDirectionVerb(
    selected
  )} vs ${comparisonLabel}: ${currentValue} vs ${baselineValue} (${delta})`;
};

const getAvailableComparisonCandidates = (primaryComparison) => {
  const selected = primaryComparison?.selected;
  const candidates = Array.isArray(primaryComparison?.candidates)
    ? primaryComparison.candidates
    : [];
  const byLens = new Map();

  [...candidates, selected].filter(Boolean).forEach((candidate) => {
    if (candidate.status !== "AVAILABLE" || !candidate.lens) return;
    if (!byLens.has(candidate.lens)) byLens.set(candidate.lens, candidate);
  });

  return Array.from(byLens.values()).sort(
    (left, right) =>
      (comparisonLensRank[left.lens] ?? 99) -
      (comparisonLensRank[right.lens] ?? 99)
  );
};

const dashboardInsightSectionIcons = {
  chart: ChartNoAxesCombined,
  comparison: TrendingUpDown,
  generated: Sparkles,
  info: Info,
  list: ListOrdered,
  products: Package,
  sparkles: Sparkles,
  table: Database,
  target: Target,
};

const DashboardInsightCollapsibleSection = ({
  children,
  defaultOpen = false,
  iconKey = "info",
  summary,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = dashboardInsightSectionIcons[iconKey] || Info;

  if (!summary) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full min-w-0 items-center justify-between sm:justify-start gap-2 rounded-md px-1 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
        >
          <Icon className="size-3 shrink-0" />
          <span className="min-w-0 max-w-[calc(100%-2rem)] truncate">
            {summary}
          </span>
          {children ? (
            <ChevronDown
              className={cn(
                "size-3 shrink-0 transition-transform",
                open && "rotate-180"
              )}
            />
          ) : null}
        </button>
      </CollapsibleTrigger>
      {children ? (
        <CollapsibleContent className="px-6 pb-1 pt-1">
          {children}
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  );
};

const DashboardInsightComparisonDetails = ({ primaryComparison }) => {
  if (!primaryComparison?.selected) return null;

  const selectedLens = primaryComparison.selected.lens;
  const candidates = getAvailableComparisonCandidates(primaryComparison);
  const summary = formatSelectedComparisonSummary(primaryComparison);

  return (
    <DashboardInsightCollapsibleSection
      iconKey="comparison"
      summary={
        summary ? `Relevant comparison: ${summary}` : "Relevant comparison"
      }
    >
      {candidates.length ? (
        <div className="overflow-x-auto pb-2 sm:pb-1">
          <div className="grid gap-y-2 text-xs text-muted-foreground">
            {candidates.map((candidate) => {
              const isSelected = candidate.lens === selectedLens;
              const currentPeriod = formatComparisonPeriodLabel(
                candidate.current
              );
              const baselinePeriod = formatComparisonPeriodLabel(
                candidate.baseline
              );
              const currentValue = formatComparisonValue(
                candidate,
                candidate.current
              );
              const baselineValue = formatComparisonValue(
                candidate,
                candidate.baseline
              );
              const delta = formatDetailPercent(candidate.deltaPct, {
                signDisplay: "exceptZero",
              });
              const valueSummary =
                currentPeriod && baselinePeriod
                  ? `${currentValue} vs ${baselineValue}`
                  : formatComparisonSummary(candidate);

              return (
                <div
                  key={candidate.lens}
                  className="grid grid-cols-[6.75rem_8.5rem_minmax(6rem,1fr)_9rem] items-baseline gap-x-4"
                >
                  <div className="font-medium text-foreground">
                    {candidate.label || candidate.lens}
                  </div>
                  <div>{baselinePeriod || "Baseline"}</div>
                  <div className="text-right tabular-nums">{valueSummary}</div>
                  <div
                    className={cn(
                      "text-right tabular-nums",
                      getComparisonDirectionClassName(candidate)
                    )}
                  >
                    <span>{delta}</span>{" "}
                    <span>{getComparisonDirectionLabel(candidate)}</span>
                    {isSelected ? (
                      <span className="text-muted-foreground"> (selected)</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </DashboardInsightCollapsibleSection>
  );
};

const DashboardInsightMetricLabel = ({ fact }) => {
  return <MetricGlossaryLabel metric={fact}>{fact.label}</MetricGlossaryLabel>;
};

const getTopProductsFact = (facts = []) =>
  facts.find(
    (fact) =>
      fact.details?.type === "top_products" &&
      Array.isArray(fact.details.rows) &&
      fact.details.rows.length
  );

const DashboardInsightTopProductsDetails = ({ fact }) => {
  if (!fact?.details?.rows?.length) return null;

  const rows = fact.details.rows.slice(0, 10);
  const firstProduct = rows[0];
  const label = fact.details.label || "Top products by sales";
  const summary = firstProduct
    ? `${label}: #${firstProduct.rank} ${firstProduct.productName} (${formatDetailPercent(
        firstProduct.share
      )})`
    : label;

  return (
    <DashboardInsightCollapsibleSection iconKey="products" summary={summary}>
      <div className="grid gap-1 text-xs text-muted-foreground">
        {rows.map((row) => (
          <div
            key={`${row.rank}-${row.productName}`}
            className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-2"
          >
            <span>#{row.rank}</span>
            <span className="min-w-0 truncate text-foreground">
              {row.productName}
            </span>
            <span className="text-right">
              {formatProductSales(row)} - {formatDetailPercent(row.share)}
            </span>
          </div>
        ))}
      </div>
    </DashboardInsightCollapsibleSection>
  );
};

const DashboardInsightSectionBlocks = ({ blocks = [] }) => (
  <div className="grid gap-2 text-xs text-muted-foreground">
    {blocks.map((block, index) => {
      if (block.type === "text") {
        return (
          <p key={`${block.type}-${index}`} className="leading-relaxed">
            {block.text}
          </p>
        );
      }

      if (block.type === "bullets") {
        return (
          <ul
            key={`${block.type}-${index}`}
            className="list-disc space-y-1 pl-4"
          >
            {block.items.map((item, itemIndex) => (
              <li key={`${itemIndex}-${item}`}>{item}</li>
            ))}
          </ul>
        );
      }

      if (block.type === "key_values") {
        return (
          <div
            key={`${block.type}-${index}`}
            className="grid gap-2 sm:grid-cols-2"
          >
            {block.rows.map((row) => (
              <div key={`${row.label}-${row.value}`}>
                <div className="text-foreground">{row.label}</div>
                <div>{row.value}</div>
              </div>
            ))}
          </div>
        );
      }

      if (block.type === "table") {
        return (
          <div key={`${block.type}-${index}`} className="overflow-x-auto">
            <table className="min-w-full text-left">
              <thead className="text-foreground">
                <tr>
                  {block.columns.map((column) => (
                    <th key={column} className="pb-1 pr-3 font-medium">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {block.columns.map((column, cellIndex) => (
                      <td key={column} className="py-0.5 pr-3">
                        {row[cellIndex] || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      return null;
    })}
  </div>
);

const DashboardInsightGeneratedSection = ({ section }) => (
  <DashboardInsightCollapsibleSection
    defaultOpen={section.defaultOpen}
    iconKey={section.iconKey}
    summary={section.summary}
  >
    <DashboardInsightSectionBlocks blocks={section.blocks} />
  </DashboardInsightCollapsibleSection>
);

const getSourceDetailLabel = (source) => {
  const sourceLabel = source?.label || source;
  const datasets = Array.isArray(source?.datasets)
    ? source.datasets.map(formatDashboardInsightLabel).join(", ")
    : "";

  return datasets ? `${sourceLabel} (${datasets})` : sourceLabel;
};

const DashboardInsightDetailsDialog = ({
  insight,
  open,
  onOpenChange,
  onAskInsight,
  onSnoozeInsight,
  onDismissInsight,
  integrationName,
}) => {
  const exportRef = useRef(null);
  const isMobile = useIsMobile();

  if (!insight) return null;

  const typeMeta = getDashboardInsightTypeMeta(insight.type);
  const tone = toneConfig[typeMeta.tone] || toneConfig.default;
  const Icon = tone.icon;
  const metricTarget = getDashboardInsightMetricTarget(insight);
  const metricHref = getRouteHref(metricTarget);
  const askRequest = buildDashboardInsightActionRequest(insight);
  const title = formatDashboardInsightText(insight.title);
  const summary = formatDashboardInsightText(insight.summary);
  const facts = getInsightFacts(insight);
  const topProductsFact = getTopProductsFact(facts);
  const primaryComparison = getDashboardInsightPrimaryComparison(insight);
  const reservedSectionIds = new Set(["generated_metadata"]);
  if (topProductsFact) {
    reservedSectionIds.add("top_products");
    reservedSectionIds.add("top-products");
  }
  if (primaryComparison) {
    reservedSectionIds.add("relevant_comparison");
    reservedSectionIds.add("relevant-comparison");
  }
  const generatedSections = getDashboardInsightPayloadSections(insight).filter(
    (section) => !reservedSectionIds.has(section.id)
  );
  const dataContext = getDashboardInsightDataContext(insight);
  const generatedAt = formatDashboardInsightRelativeTime(
    dataContext.generatedAt
  );
  const rangeSummary = getDashboardInsightRangeSummary(insight);
  const sourceDetails = Array.isArray(dataContext.sources)
    ? dataContext.sources.map(getSourceDetailLabel).filter(Boolean)
    : [];
  const metricLabels = facts.map((fact) => fact.label).filter(Boolean);
  const sourceCount = sourceDetails.length || dataContext.sources?.length || 0;
  const displayedSourceCount = sourceCount || 1;
  const sourceCountLabel = `${displayedSourceCount} data ${
    displayedSourceCount === 1 ? "source" : "sources"
  }`;
  const metadataSummary = [
    generatedAt ? `Generated ${generatedAt}` : null,
    `explored ${sourceCountLabel}`,
    rangeSummary,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="md:max-w-2xl flex flex-col">
        <ScrollArea
          type={isMobile ? "always" : undefined}
          className="h-full flex-1 min-h-0 pr-3"
          viewportClassName="max-h-[60vh]"
        >
          <div className="p-px">
            <div
              className="group bg-background text-foreground data-[exporting=true]:rounded-2xl data-[exporting=true]:bg-muted data-[exporting=true]:p-4 sm:data-[exporting=true]:p-6"
              ref={exportRef}
            >
              <div className="space-y-4 group-data-[exporting=true]:rounded-xl group-data-[exporting=true]:border group-data-[exporting=true]:bg-card group-data-[exporting=true]:p-5 group-data-[exporting=true]:shadow-xl sm:group-data-[exporting=true]:p-6">
                <ResponsiveDialogHeader>
                  <div className="mb-2 flex items-center justify-between gap-3 sm:pr-8">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <DashboardInsightTypeBadge
                        icon={Icon}
                        label={typeMeta.label}
                        description={typeMeta.description}
                        className={tone.iconClassName}
                      />
                      <DashboardInsightPriorityBadge insight={insight} />
                      <DashboardInsightChangeBadge insight={insight} />
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <DashboardInsightActionsMenu
                        insight={insight}
                        onSnoozeInsight={onSnoozeInsight}
                        onDismissInsight={onDismissInsight}
                      />
                      <DataExportButton
                        imageExportRef={exportRef}
                        title={title}
                        integrationName={integrationName}
                        asIcon
                        label="Share insight"
                        menuLabel="Share insight"
                        itemLabel="insight"
                        slackLabel="Share to Slack"
                        className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
                        contentClassName="z-[70]"
                        subContentClassName="z-[70]"
                        sendSeparately
                      />
                    </div>
                  </div>
                  <ResponsiveDialogTitle>
                    {title}

                    {integrationName ? (
                      <span data-export-show className="hidden">
                        {` (${integrationName})`}
                      </span>
                    ) : null}
                  </ResponsiveDialogTitle>
                  {summary ? (
                    <ResponsiveDialogDescription className="text-sm leading-relaxed">
                      {summary}
                    </ResponsiveDialogDescription>
                  ) : null}
                </ResponsiveDialogHeader>

                {facts.length ? (
                  <div className="grid gap-2 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
                    {facts.map((fact) => (
                      <div key={fact.key} className="min-w-0">
                        <div className="truncate text-xs text-muted-foreground">
                          <DashboardInsightMetricLabel fact={fact} />
                        </div>
                        <div className="text-sm font-medium">{fact.value}</div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="space-y-0.5">
                  <DashboardInsightTopProductsDetails fact={topProductsFact} />

                  <DashboardInsightComparisonDetails
                    primaryComparison={primaryComparison}
                  />

                  {generatedSections.map((section) => (
                    <DashboardInsightGeneratedSection
                      key={section.id}
                      section={section}
                    />
                  ))}

                  <DashboardInsightCollapsibleSection
                    iconKey="generated"
                    summary={metadataSummary || "Generated from store data"}
                  >
                    <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                      <div>
                        <div className="text-foreground">Generated</div>
                        <div>{generatedAt || "recently"}</div>
                      </div>
                      <div>
                        <div className="text-foreground">Source</div>
                        <div>{sourceDetails.join(", ") || "Store data"}</div>
                      </div>
                      <div>
                        <div className="text-foreground">Date range</div>
                        <div>
                          {rangeSummary || "Latest available store data"}
                        </div>
                      </div>
                      <div>
                        <div className="text-foreground">Timezone</div>
                        <div>{dataContext.timezone || "UTC"}</div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-foreground">Metrics</div>
                        <div>
                          {metricLabels.slice(0, 5).join(", ") ||
                            "Relevant audit metrics"}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        AI-assisted from connected store data and performance
                        benchmarks. Use it as decision support.
                      </div>
                    </div>
                  </DashboardInsightCollapsibleSection>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        <ResponsiveDialogFooter className="shrink-0 gap-2 sm:justify-between">
          {metricHref ? (
            <Button asChild variant="outline" className="gap-2">
              <Link to={metricHref} onClick={() => onOpenChange(false)}>
                {metricTarget.label}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          ) : null}
          {askRequest ? (
            <Button
              type="button"
              className="gap-2"
              onClick={() => {
                onOpenChange(false);
                onAskInsight?.(askRequest);
              }}
            >
              <MessageSquare className="size-4" />
              Ask AI
            </Button>
          ) : null}
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};

export const DashboardInsightsPanel = ({
  insights = [],
  loading,
  onAskInsight,
  onSnoozeInsight,
  onDismissInsight,
  integrationName,
}) => {
  const [selectedInsight, setSelectedInsight] = useState(null);
  const renderableInsights = useMemo(
    () =>
      insights
        .filter((insight) => insight?.type !== "DIAGNOSTIC_GAP")
        .slice()
        .sort(compareDashboardInsightsByPriority),
    [insights]
  );

  if (loading) return <DashboardInsightsSkeleton />;
  if (!renderableInsights.length) return null;

  const handleSnoozeInsight = async (insight) => {
    await onSnoozeInsight?.(insight);
    if (selectedInsight?.id === insight?.id) setSelectedInsight(null);
  };

  const handleDismissInsight = async (insight) => {
    await onDismissInsight?.(insight);
    if (selectedInsight?.id === insight?.id) setSelectedInsight(null);
  };

  return (
    <section className="min-w-0 space-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold">
        <Sparkles className="size-4 shrink-0 text-primary" />
        <span>Actionable Insights</span>
        <span className="text-xs font-normal text-muted-foreground">
          AI-assisted from recent store audits
        </span>
      </div>

      <div className="max-w-full overflow-x-auto pb-1">
        <div className="flex gap-3">
          {renderableInsights.map((insight) => (
            <DashboardInsightCard
              key={insight.id}
              insight={insight}
              onOpenInsight={setSelectedInsight}
              onAskInsight={onAskInsight}
              onSnoozeInsight={handleSnoozeInsight}
              onDismissInsight={handleDismissInsight}
            />
          ))}
        </div>
      </div>

      <DashboardInsightDetailsDialog
        insight={selectedInsight}
        open={Boolean(selectedInsight)}
        onOpenChange={(open) => {
          if (!open) setSelectedInsight(null);
        }}
        onAskInsight={onAskInsight}
        onSnoozeInsight={handleSnoozeInsight}
        onDismissInsight={handleDismissInsight}
        integrationName={integrationName}
      />
    </section>
  );
};

const StoreDashboardInsightsBand = ({ selectedIntegration }) => {
  const [insights, setInsights] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [, setAssistantLaunchRequest] = useAtom(assistantLaunchRequestAtom);
  const { queryApi, loading } = useApiQuery({
    query: STORE_INSIGHTS_QUERY,
    onError: () => "",
  });
  const { mutationApi: snoozeInsight } = useApiMutation({
    mutation: SNOOZE_STORE_INSIGHT_MUTATION,
    onError: () => "",
  });
  const { mutationApi: dismissInsight } = useApiMutation({
    mutation: DISMISS_STORE_INSIGHT_MUTATION,
    onError: () => "",
  });

  useEffect(() => {
    if (!selectedIntegration?.id) {
      setInsights([]);
      setHasLoaded(true);
      return;
    }

    let active = true;
    setHasLoaded(false);

    queryApi({
      integrationId: selectedIntegration.id,
      limit: DASHBOARD_INSIGHT_LIMIT,
      surface: "DASHBOARD",
    })
      .then(({ data }) => {
        if (!active) return;

        setInsights(
          (data?.storeInsights || []).map((insight) => ({
            ...insight,
            currency: selectedIntegration.currency,
          }))
        );
        setHasLoaded(true);
      })
      .catch(() => {
        if (!active) return;

        setInsights([]);
        setHasLoaded(true);
      });

    return () => {
      active = false;
    };
  }, [queryApi, selectedIntegration?.id, selectedIntegration?.currency]);

  const visibleInsights = useMemo(
    () =>
      insights.filter(
        (insight) =>
          insight?.id && insight?.title && insight.type !== "DIAGNOSTIC_GAP"
      ),
    [insights]
  );

  const handleAskInsight = (actionRequest) => {
    const request = createAssistantPromptRequest({
      integrationId: selectedIntegration?.id,
      ...actionRequest,
      autoSend: true,
    });

    if (request) {
      setAssistantLaunchRequest(request);
    }
  };

  const removeInsight = (insightId) => {
    setInsights((currentInsights) =>
      currentInsights.filter((insight) => insight.id !== insightId)
    );
  };

  const handleSnoozeInsight = async (insight) => {
    if (!insight?.id) return;
    await snoozeInsight({ id: insight.id });
    removeInsight(insight.id);
  };

  const handleDismissInsight = async (insight) => {
    if (!insight?.id) return;
    await dismissInsight({ id: insight.id });
    removeInsight(insight.id);
  };

  return (
    <DashboardInsightsPanel
      insights={visibleInsights}
      loading={!hasLoaded || loading}
      onAskInsight={handleAskInsight}
      onSnoozeInsight={handleSnoozeInsight}
      onDismissInsight={handleDismissInsight}
      integrationName={
        selectedIntegration?.description || selectedIntegration?.domain
      }
    />
  );
};

export const DashboardInsightsBand = () => {
  const { selectedIntegration } = useIntegration(
    storeIntegrationTypes.shopify,
    { queryOnRender: false }
  );
  return (
    <StoreDashboardInsightsBand
      key={`${selectedIntegration?.id}:${selectedIntegration?.currency}`}
      selectedIntegration={selectedIntegration}
    />
  );
};
